import React, { useState } from 'react';
import { trackingAPI } from '../services/api';
import { formatDateTime } from '../utils/helpers';
import toast from 'react-hot-toast';

// ── Event config map ──────────────────────────────────────────────────────────
const EVENT_CONFIG = {
  RECEIVED_CHINA:    { color: '#3b82f6', icon: '📦', label: 'Received at China Warehouse' },
  SORTED:            { color: '#8b5cf6', icon: '✔',  label: 'Sorted & Verified' },
  BARCODE_GENERATED: { color: '#06b6d4', icon: '⬡',  label: 'Barcode Sticker Generated' },
  GROUPED:           { color: '#06b6d4', icon: '▣',  label: 'Grouped into Shipment' },
  DEPARTED:          { color: '#f59e0b', icon: '🚢', label: 'Departed from Port' },
  IN_TRANSIT:        { color: '#8b5cf6', icon: '◎',  label: 'In Transit' },
  ARRIVED_BD:        { color: '#06b6d4', icon: '⚓', label: 'Arrived at Bangladesh Port' },
  SCANNED_BD:        { color: '#10b981', icon: '⟳',  label: 'Scanned at BD Warehouse' },
  DELIVERED:         { color: '#10b981', icon: '✓',  label: 'Delivered to Customer' },
};

const getEventCfg = (type) =>
  EVENT_CONFIG[type] || { color: '#3b82f6', icon: '◈', label: type };

// ── Journey steps for progress bar ───────────────────────────────────────────
const STEPS = [
  { key: 'RECEIVED_CHINA',    label: 'Received',  sub: 'China WH' },
  { key: 'SORTED',            label: 'Sorted',    sub: 'Verified' },
  { key: 'BARCODE_GENERATED', label: 'Barcode',   sub: 'Sticker' },
  { key: 'GROUPED',           label: 'Grouped',   sub: 'Shipment' },
  { key: 'DEPARTED',          label: 'Departed',  sub: 'Port' },
  { key: 'IN_TRANSIT',        label: 'Transit',   sub: 'At Sea' },
  { key: 'ARRIVED_BD',        label: 'Arrived',   sub: 'BD Port' },
  { key: 'SCANNED_BD',        label: 'Scanned',   sub: 'BD WH' },
  { key: 'DELIVERED',         label: 'Delivered', sub: 'Customer' },
];

// ── Status badge helpers ──────────────────────────────────────────────────────
const cartonBadge = (s) => ({
  RECEIVED: 'badge-yellow', SORTING: 'badge-purple',
  REGISTERED: 'badge-blue', GROUPED: 'badge-cyan',
  DEPARTED: 'badge-blue', IN_TRANSIT: 'badge-purple',
  ARRIVED_BD: 'badge-yellow', IN_BD_WAREHOUSE: 'badge-green',
  DELIVERED: 'badge-green',
}[s] || 'badge-gray');

const shipmentBadge = (s) => ({
  CREATED: 'badge-gray', PACKING: 'badge-yellow',
  READY: 'badge-blue', DEPARTED: 'badge-purple',
  IN_TRANSIT: 'badge-cyan', ARRIVED: 'badge-yellow',
  COMPLETED: 'badge-green',
}[s] || 'badge-gray');

// ── Progress bar component ────────────────────────────────────────────────────
function ProgressBar({ events }) {
  const doneTypes = new Set((events || []).map(e => e.eventType));
  const lastIdx   = STEPS.reduce((acc, s, i) => doneTypes.has(s.key) ? i : acc, -1);

  return (
    <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 580 }}>
        {STEPS.map((step, i) => {
          const done   = doneTypes.has(step.key);
          const active = i === lastIdx;
          const clr    = done ? (active ? 'var(--accent)' : 'var(--success)') : 'var(--text-muted)';
          return (
            <React.Fragment key={step.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 52 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: done ? clr : 'var(--bg-input)',
                  border: `2px solid ${done ? clr : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, color: done ? '#fff' : 'var(--text-muted)',
                  boxShadow: active ? `0 0 14px ${clr}60` : 'none',
                  transition: 'all 0.3s',
                }}>
                  {done ? (active ? '●' : '✓') : i + 1}
                </div>
                <div style={{ fontSize: 9, color: clr, marginTop: 4, textAlign: 'center', fontWeight: done ? 700 : 400 }}>{step.label}</div>
                <div style={{ fontSize: 8, color: 'var(--text-muted)', textAlign: 'center' }}>{step.sub}</div>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{
                  height: 2, flex: 1,
                  background: i < lastIdx ? 'var(--success)' : 'var(--border)',
                  marginTop: 14, transition: 'background 0.3s',
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── Info row helper ───────────────────────────────────────────────────────────
function InfoRow({ label, value, mono, accent, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, marginRight: 8 }}>{label}</span>
      {children || (
        <span style={{
          fontSize: 12,
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
          color: accent ? 'var(--accent)' : 'var(--text-primary)',
          textAlign: 'right', maxWidth: 200,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{value || '—'}</span>
      )}
    </div>
  );
}

// ── Carton + Shipment info cards ──────────────────────────────────────────────
function InfoCards({ tl }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
      {/* Carton */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, fontWeight: 600 }}>Carton</div>
        <InfoRow label="GRN" value={tl.cartonGrn} mono accent />
        <InfoRow label="Barcode" value={tl.cartonBarcode} mono accent />
        <InfoRow label="Description" value={tl.productDescription} />
        <InfoRow label="Customer ID" value={tl.customerId} mono />
        <InfoRow label="Status">
          <span className={`badge ${cartonBadge(tl.cartonStatus)}`} style={{ fontSize: 10 }}>{tl.cartonStatus}</span>
        </InfoRow>
        {tl.currentLocation && (
          <InfoRow label="Location" value={`📍 ${tl.currentLocation}`} />
        )}
      </div>

      {/* Shipment */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, fontWeight: 600 }}>Shipment</div>
        {tl.shipmentNumber ? (
          <>
            <InfoRow label="Shipment No." value={tl.shipmentNumber} mono accent />
            <InfoRow label="Port" value={tl.departurePort?.replace('_PORT', ' Port')} />
            <InfoRow label="Mode" value={tl.shipmentMode} />
            <InfoRow label="Vessel" value={tl.vesselName} />
            <InfoRow label="BL Number" value={tl.blNumber} mono />
            <InfoRow label="Status">
              <span className={`badge ${shipmentBadge(tl.shipmentStatus)}`} style={{ fontSize: 10 }}>{tl.shipmentStatus}</span>
            </InfoRow>
          </>
        ) : (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, paddingTop: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>⬡</div>
            Not assigned to any shipment yet
          </div>
        )}
      </div>
    </div>
  );
}

// ── Timeline events ───────────────────────────────────────────────────────────
function TimelineList({ events }) {
  if (!events || events.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">◎</div>
        <div className="empty-state-text">No tracking events recorded yet</div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          Events are recorded automatically as the carton moves through the system.
        </p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', paddingLeft: 2 }}>
      {/* Gradient vertical line */}
      <div style={{
        position: 'absolute', left: 15, top: 16, bottom: 16,
        width: 2,
        background: 'linear-gradient(to bottom, var(--accent), var(--success))',
        opacity: 0.25, borderRadius: 2,
      }} />

      {events.map((ev, i) => {
        const cfg    = getEventCfg(ev.eventType);
        const isLast = i === events.length - 1;
        return (
          <div key={ev.id} style={{
            display: 'flex', gap: 14,
            marginBottom: isLast ? 0 : 18,
            animation: `fadeIn 0.35s ease ${i * 60}ms both`,
          }}>
            {/* Circle icon */}
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: `${cfg.color}18`,
              border: `2px solid ${isLast ? cfg.color : cfg.color + '60'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, zIndex: 1,
              boxShadow: isLast ? `0 0 18px ${cfg.color}50` : 'none',
            }}>
              {cfg.icon}
            </div>

            {/* Event card */}
            <div style={{
              flex: 1,
              background: isLast ? `${cfg.color}08` : 'var(--bg-secondary)',
              border: `1px solid ${isLast ? cfg.color + '50' : 'var(--border)'}`,
              borderRadius: 10, padding: '12px 16px',
            }}>
              {/* Top row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11,
                    color: cfg.color, fontWeight: 700,
                    background: `${cfg.color}18`,
                    padding: '2px 8px', borderRadius: 4,
                  }}>{ev.eventType}</span>
                  {isLast && (
                    <span style={{ fontSize: 10, color: cfg.color, fontWeight: 700 }}>● NOW</span>
                  )}
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {formatDateTime(ev.createdAt)}
                </span>
              </div>

              {/* Label */}
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginTop: 6 }}>
                {cfg.label}
              </div>

              {/* Description */}
              {ev.description && (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 5, lineHeight: 1.55 }}>
                  {ev.description}
                </p>
              )}

              {/* Footer chips */}
              <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                {ev.location && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    📍 {ev.location}
                  </span>
                )}
                {ev.shipmentNumber && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    ⬡ {ev.shipmentNumber}
                  </span>
                )}
                {ev.cartonGrn && !ev.shipmentNumber && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    ▣ {ev.cartonGrn}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Tracking() {
  const [barcode,  setBarcode]  = useState('');
  const [timeline, setTimeline] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    const b = barcode.trim();
    if (!b) return;
    setLoading(true);
    setSearched(true);
    setTimeline(null);
    try {
      const res = await trackingAPI.trackPublic(b);
      const data = res.data.data;
      setTimeline(data);
      if (!data?.events?.length) {
        toast('Carton found but no tracking events yet', { icon: 'ℹ️' });
      } else {
        toast.success(`Found ${data.events.length} tracking events`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Barcode not found');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setBarcode(''); setTimeline(null); setSearched(false);
  };

  return (
    <div className="animate-fade">
      <div className="page-header">
        <h1 className="page-title">Shipment Tracking</h1>
        <p className="page-subtitle">
          Full journey timeline — public endpoint, no login needed
        </p>
      </div>

      {/* Search box */}
      <div className="card" style={{ maxWidth: 660, marginBottom: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 14 }}>
          Track by Barcode
        </h3>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10 }}>
          <input
            className="form-input"
            value={barcode}
            onChange={e => setBarcode(e.target.value)}
            placeholder="e.g.  CGO-1-1234567890"
            style={{ flex: 1, fontFamily: 'var(--font-mono)', letterSpacing: '0.04em' }}
            autoFocus
          />
          {searched && (
            <button type="button" className="btn btn-secondary" onClick={handleClear}>
              Clear
            </button>
          )}
          <button type="submit" className="btn btn-primary" disabled={loading || !barcode.trim()}>
            {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '◎'}
            {loading ? 'Tracking...' : 'Track'}
          </button>
        </form>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
          Customers can use this link directly without any account to track their cargo.
        </p>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0', color: 'var(--text-muted)' }}>
          <span className="spinner" />
          <span style={{ fontSize: 14 }}>Fetching journey data...</span>
        </div>
      )}

      {/* No result */}
      {searched && !loading && !timeline && (
        <div className="card" style={{ maxWidth: 660 }}>
          <div className="empty-state">
            <div className="empty-state-icon">◎</div>
            <div className="empty-state-text">No carton found for this barcode</div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              Barcodes look like: <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>CGO-1-1234567890</code>
            </p>
          </div>
        </div>
      )}

      {/* Result */}
      {timeline && !loading && (
        <div style={{ maxWidth: 760, animation: 'fadeIn 0.4s ease' }}>

          {/* Progress bar */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15 }}>Journey Progress</h3>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', background: 'var(--accent-glow)', padding: '3px 10px', borderRadius: 20 }}>
                {timeline.events?.length || 0} events
              </span>
            </div>
            <ProgressBar events={timeline.events} />
          </div>

          {/* Carton + shipment info */}
          <InfoCards tl={timeline} />

          {/* Timeline */}
          <div className="card">
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 20 }}>
              Tracking Timeline
            </h3>
            <TimelineList events={timeline.events} />
          </div>

        </div>
      )}
    </div>
  );
}
